import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

import type {
  HistoryEvent,
  ClientInteraction,
} from '@services/requests/housien-services/ClientInsightsService';
import {
  tryCreateInteraction,
  tryDeleteInteraction,
  runFetchPage,
  type FetchPageSetters,
} from './useClientHistory.helpers';

interface UseClientHistoryParams {
  agencyId: string;
  clientId: string;
  pageSize?: number;
}

interface UseClientHistoryResult {
  events: HistoryEvent[];
  isLoading: boolean;
  isError: boolean;
  page: number;
  totalPages: number;
  totalItems: number;
  hasMore: boolean;
  loadMore: () => void;
  logInteraction: (body: Partial<ClientInteraction>) => Promise<boolean>;
  deleteInteraction: (interactionId: string) => Promise<boolean>;
  refetch: () => void;
}

/**
 * Manages paginated client history (interactions, visits, feedback) with
 * load-more, optimistic logging, deletion, and refetch.
 *
 * @param params - Hook parameters.
 * @param params.agencyId - The agency identifier.
 * @param params.clientId - The client identifier.
 * @param params.pageSize - Page size for history fetches (defaults to 20).
 * @returns History events, loading/error flags, pagination info, and actions.
 */
export function useClientHistory({
  agencyId,
  clientId,
  pageSize = 20,
}: UseClientHistoryParams): UseClientHistoryResult {
  const [events, setEvents] = useState<HistoryEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const cancelRef = useRef<{ cancel: () => void } | null>(null);

  const setters: FetchPageSetters = useMemo(
    () => ({ setEvents, setPage, setTotalPages, setTotalItems, setIsLoading, setIsError }),
    [],
  );

  const fetchPage = useCallback(
    (targetPage: number, append: boolean) =>
      runFetchPage({ agencyId, clientId, pageSize, targetPage, append, setters, cancelRef }),
    [agencyId, clientId, pageSize, setters],
  );

  useEffect(() => {
    fetchPage(1, false);
    return () => { cancelRef.current?.cancel(); };
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (page < totalPages) fetchPage(page + 1, true);
  }, [page, totalPages, fetchPage]);

  const logInteraction = useCallback(
    async (body: Partial<ClientInteraction>): Promise<boolean> => {
      const ok = await tryCreateInteraction(agencyId, clientId, body);
      if (ok) fetchPage(1, false);
      return ok;
    },
    [agencyId, clientId, fetchPage],
  );

  const deleteInteraction = useCallback(
    async (interactionId: string): Promise<boolean> => {
      const ok = await tryDeleteInteraction(agencyId, clientId, interactionId);
      if (ok) fetchPage(1, false);
      return ok;
    },
    [agencyId, clientId, fetchPage],
  );

  const refetch = useCallback(() => {
    setEvents([]);
    fetchPage(1, false);
  }, [fetchPage]);

  return {
    events, isLoading, isError, page, totalPages, totalItems,
    hasMore: page < totalPages, loadMore, logInteraction, deleteInteraction, refetch,
  };
}
